import { Flex } from '@mantine/core';
import type { RefObject } from 'react';
import { useTranslation } from 'react-i18next';
import { exportChart } from '../../../../helpers/image-downloader/image-downloader';
import { useGlobalStore } from '../../../../store/global-store/global-store';
import {
  setInitiativeCompletionMetric,
  setInitiativeFocusMetric,
} from '../../../../store/process-analysis-store/process-analysis-store.actions';
import { useProcessAnalysisStore } from '../../../../store/process-analysis-store/process-analysis-store';
import { baseWhite, skyDark } from '../../../../styles/design-tokens';
import { Button } from '../../../../ui-library/button/button';
import { Icon } from '../../../../ui-library/icon/icon';
import { FloraButton } from '../../../flora/flora-button';
import { useFloraData } from '../../hooks/use-flora-data';
import { MetricType, ViewMode } from '../../process-analysis.type';
import type { ChartType } from '../../process-analysis.type';
import { ChartTypeToggle } from './chart-type-toggle';
import { MetricSwitcher } from '../metric-switcher/metric-switcher';

type Props = {
  activeChartType?: ChartType;
  onChartTypeChange?: (chartType: ChartType) => void;
  chartRef?: RefObject<HTMLDivElement | null>;
  disableControls?: boolean;
  onExportCSV?: () => void;
};

export const MainSectionControls = ({
  activeChartType,
  onChartTypeChange,
  chartRef,
  disableControls,
  onExportCSV,
}: Props) => {
  const i18n = useTranslation();
  const tab = useProcessAnalysisStore((state) => state.activeTab);
  const viewMode = useProcessAnalysisStore((state) => state.viewMode);
  const initiativeCompletionMetric = useProcessAnalysisStore((state) => state.initiativeCompletionMetric);
  const initiativeFocusMetric = useProcessAnalysisStore((state) => state.initiativeFocusMetric);
  const { useAI } = useGlobalStore();
  const floraData = useFloraData();

  const showMetricSwitcher = viewMode === ViewMode.InitiativeCompletion || viewMode === ViewMode.InitiativeFocus;

  const handleMetricChange = (metric: MetricType) => {
    if (viewMode === ViewMode.InitiativeCompletion) {
      setInitiativeCompletionMetric(metric);
    } else if (viewMode === ViewMode.InitiativeFocus) {
      setInitiativeFocusMetric(metric);
    }
  };

  const currentMetric = viewMode === ViewMode.InitiativeCompletion ? initiativeCompletionMetric : initiativeFocusMetric;

  const handleExport = async () => {
    // If we have CSV export function, use it; otherwise export chart
    if (onExportCSV) {
      try {
        onExportCSV();
      } catch (error) {
        console.error('Error exporting CSV:', error);
      }
    } else if (chartRef?.current) {
      try {
        await exportChart(chartRef.current, `${tab}_chart.png`);
      } catch (error) {
        console.error('Error exporting chart:', error);
      }
    }
  };

  return (
    <Flex align="center" gap={24}>
      {activeChartType && onChartTypeChange && (
        <ChartTypeToggle activeChartType={activeChartType} onChartTypeChange={onChartTypeChange} />
      )}
      {showMetricSwitcher && <MetricSwitcher value={currentMetric} onChange={handleMetricChange} />}
      {useAI && floraData && (
        <FloraButton externalData={floraData.data} prompt={floraData.prompt} tooltipText={floraData.tooltip} />
      )}
      <Button
        variant="secondary"
        radius="xl"
        size="xs"
        leftSection={
          <Icon
            name="download"
            color={disableControls ? skyDark : baseWhite}
            variant="filled"
            size={16}
            style={{ cursor: disableControls ? 'not-allowed' : 'pointer' }}
          />
        }
        onClick={handleExport}
        disabled={disableControls}
      >
        {i18n.t('common.actions.export')}
      </Button>
    </Flex>
  );
};
